import { http } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import type { TourRole } from '@/components/tour/tours';

function storageKey(tipo: TourRole): string {
  return `wenkey-tour-seen-${tipo}`;
}

/**
 * User who already opened a tour in this page load.
 * Scoped to the account so a later login in the same tab still gets its own tour,
 * while a replay started before the auto-start timer does not open a second one.
 */
let engagedUserId: string | null = null;

/**
 * Two layers: localStorage so a failed request still hides the tour, and
 * `POST /utenti/me/tour-completato` so a new browser follows the account.
 */
export const tourService = {
  noteStarted(): void {
    engagedUserId = useAuthStore.getState().user?.id ?? null;
  },

  wasStarted(): boolean {
    const id = useAuthStore.getState().user?.id ?? null;
    return id !== null && engagedUserId === id;
  },

  remember(tipo: TourRole): void {
    try {
      localStorage.setItem(storageKey(tipo), '1');
    } catch {
      /* Private mode can reject storage; the backend stamp still applies. */
    }
  },

  hasSeen(tipo: TourRole): boolean {
    try {
      return localStorage.getItem(storageKey(tipo)) === '1';
    } catch {
      return false;
    }
  },

  async markCompleted(tipo: TourRole): Promise<void> {
    this.remember(tipo);
    try {
      await http.post<void, { tipo: TourRole }>('/utenti/me/tour-completato', { tipo });
      const user = useAuthStore.getState().user;
      if (!user) return;
      const field = tipo === 'staff' ? 'tour_staff_completato_at' : 'tour_cliente_completato_at';
      if (user[field]) return;
      useAuthStore.getState().setUser({ ...user, [field]: new Date().toISOString() });
    } catch (error) {
      console.warn('[tour] backend save failed', error);
    }
  },
};
